import React, { useEffect, useState, useCallback, useMemo, useRef } from "react";
import { createPortal } from "react-dom";
//@ts-ignore
import MicRecorder from "mic-recorder-to-mp3";
import { HiSpeakerphone } from "react-icons/hi";
import { BsFillRecordFill, BsFillStopFill } from "react-icons/bs";
import { CiNoWaitingSign } from "react-icons/ci";
import { Toaster, toast } from "react-hot-toast";
import * as Tooltip from "@radix-ui/react-tooltip";
import { IThing, IThingIntegration } from "@/data/interfaces/thing";
import AlphaTalkdownDialog from "@/components/dialogs/AlphaTalkdownDialog";
import AudioFileDropZone from "@/components/camera/AudioFileDropZone";
import SoundboardPanel from "@/components/camera/SoundboardPanel";
import { SoundboardItem } from "@/utils/soundboardStorage";
import universalPreRecordedAudio from "@/data/calls/operator/universalPreRecordedAudio";
import {
    sendLiveTalkdownChunk,
    startLiveTalkdownSession,
    stopLiveTalkdownSession
} from "@/data/calls/milestone/startLiveTalkdown";
import getSpeakersForCamera from '@/data/calls/turingCloud/getSpeakersForCamera';
import { fetchTalkdownLibrary, fetchTalkdownAudioUrl, fetchTalkdownCategories, TalkdownFile, TalkdownCategory } from "@/data/calls/alert/talkdownApi";
import getEagleEyeCloudSingleCameraAsync from '@/data/calls/eagleeyecloud/getSingleCameraAsync';
import getHanwhaSingleCameraAsync from '@/data/calls/hanwha/getSingleCameraAsync';
import getTalkdownCameraSpeakerSupport from "@/data/calls/avigilonalta/getTalkdownCameraSpeakerSupport";
import getUnityTalkdownCameraSpeakerSupport from "@/data/calls/avigilonunity/getTalkdownCameraSpeakerSupport";
import getAxisTalkdownCameraSpeakerSupport from "@/data/calls/axiscloudconnect/getAxisTalkdownCameraSpeakerSupport";
import logUserAction from "@/data/calls/userLogExtended/logUserAction";
import { UserLogExtendedActionEventTypes } from "@/data/enums/userLogExtended.enums";
import { ICamera } from "@/data/interfaces/camera";
import { isAltaVirtualRoutingCamera } from "@/components/queue/queueTalkdownRouting";

const recorder = new MicRecorder({
    bitRate: 128,
});

const PREVIEW_CACHE_TTL_MS = 5 * 60 * 1000;
const hasIntegrationName = (device: any, keyword: string) =>
    String(device?.location_description || "").includes(keyword) ||
    String(device?.general_description || "").includes(keyword);
const isAlphaCamera = (device: any) => hasIntegrationName(device, "AlphaVision");
const isMilestoneDevice = (device: any) => hasIntegrationName(device, "Milestone");
const isAvigilonAltaCamera = (device: any) => hasIntegrationName(device, "AvigilonAlta");
const getAltaVendorCameraId = (camera: any) => {
    const direct = camera?.username ?? camera?.vendorCameraId ?? camera?.VendorCameraId;
    if (direct) return String(direct);

    const description = String(camera?.general_description || "");
    const match = description.match(/AvigilonAlta Camera:\s*([^\s]+)/i);
    return match?.[1] || "";
};
const isAvigilonUnityCamera = (device: any) => hasIntegrationName(device, "AvigilonUnity");
const getUnityVendorCameraId = (camera: any) => {
    const direct = camera?.username ?? camera?.vendorCameraId ?? camera?.VendorCameraId;
    if (direct) return String(direct);

    const description = String(camera?.general_description || "");
    const match = description.match(/AvigilonUnity Camera:\s*([^\s]+)/i);
    return match?.[1] || "";
};

const isAxisCloudCamera = (device: any) => hasIntegrationName(device, "AxisCloud");
const getAxisVendorCameraId = (camera: any) => {
    const direct = camera?.username ?? camera?.vendorCameraId ?? camera?.VendorCameraId;
    if (direct) return String(direct);

    const description = String(camera?.general_description || "");
    const match = description.match(/AxisCloud Camera:\s*([^\s]+)/i);
    return match?.[1] || "";
};

const getUnitySiteUniqueId = (camera: any) => (camera?.password ? String(camera.password) : "");
const getAipxCameraGrainId = (camera: any) => {
    const direct = camera?.externalCameraId ?? camera?.external_camera_id ?? camera?.aipxGrainId ?? camera?.AipxGrainId;
    return direct ? String(direct) : "";
};
const isTuringCloudDevice = (device: any) => hasIntegrationName(device, "TuringCloud");

const MILESTONE_TALKDOWN_SAMPLE_RATE = 8000;
const MILESTONE_TALKDOWN_BUFFER_SIZE = 2048;
const MILESTONE_TALKDOWN_UPLOAD_CHUNK_BYTES = 3200;

const convertFloat32ToMilestonePcm = (
    inputData: Float32Array,
    inputSampleRate: number,
    outputSampleRate: number = MILESTONE_TALKDOWN_SAMPLE_RATE
) => {
    if (!inputData.length || !inputSampleRate) {
        return new Uint8Array();
    }

    const sampleRateRatio = inputSampleRate / outputSampleRate;
    const outputLength = Math.max(1, Math.floor(inputData.length / sampleRateRatio));
    const output = new Uint8Array(outputLength * 2);

    for (let i = 0; i < outputLength; i += 1) {
        const sourceIndex = Math.min(inputData.length - 1, Math.floor(i * sampleRateRatio));
        const sample = Math.max(-1, Math.min(1, inputData[sourceIndex]));
        const pcmValue = sample < 0 ? sample * 0x8000 : sample * 0x7fff;
        const rounded = Math.round(pcmValue);
        output[i * 2] = rounded & 0xff;
        output[i * 2 + 1] = (rounded >> 8) & 0xff;
    }

    return output;
};

const VOICE_FORGE_ENABLED = false;

const CameraInfoToast = ({
    camera,
    onDismiss,
    noSpeakerSupport = false
}: {
    camera: any;
    onDismiss: () => void;
    noSpeakerSupport?: boolean;
}) => {
    const [progress, setProgress] = useState(100);

    useEffect(() => {
        const totalDuration = 30000;
        const updateInterval = 100;
        const totalSteps = totalDuration / updateInterval;
        const decrement = 100 / totalSteps;

        const timer = setInterval(() => {
            setProgress((oldProgress) => {
                const newProgress = oldProgress - decrement;
                if (newProgress <= 0) {
                    clearInterval(timer);
                    return 0;
                }
                return newProgress;
            });
        }, updateInterval);

        return () => {
            clearInterval(timer);
        };
    }, []);

    return (
        <div className="flex flex-col gap-1 w-full">
            <div className="flex justify-between items-center">
                <p><b>Name:</b> {camera.name}</p>
                <button onClick={onDismiss} className="ml-4 p-1 rounded-full hover:bg-gray-200">
                    &#x2715;
                </button>
            </div>

            {noSpeakerSupport && (
                <div className="rounded-md border border-red-300 bg-red-100 px-2 py-1 text-sm text-red-700 font-medium">
                    🚫 No speaker support for this camera
                </div>
            )}

            <p><b>DB Cam ID:</b> {camera.id}</p>
            <p><b>Int Cam ID:</b> {camera.username}</p>
            <p><b>Gen Desc:</b> {camera.general_description}</p>
            <p><b>Loc Desc:</b> {camera.location_description}</p>

            <div className="w-full bg-gray-200 rounded-full h-1 mt-2">
                <div
                    className={`${noSpeakerSupport ? 'bg-red-500' : 'bg-blue-500'} h-1 rounded-full`}
                    style={{ width: `${progress}%` }}
                />
            </div>
        </div>
    );
};

const ThingInfoToast = ({ thing, onDismiss }: { thing: IThing, onDismiss: () => void }) => {
    const [progress, setProgress] = useState(100);

    useEffect(() => {
        const totalDuration = 30000; // 30 seconds
        const updateInterval = 100; // 100ms
        const totalSteps = totalDuration / updateInterval;
        const decrement = 100 / totalSteps;

        const timer = setInterval(() => {
            setProgress((oldProgress) => {
                const newProgress = oldProgress - decrement;
                if (newProgress <= 0) {
                    clearInterval(timer);
                    return 0;
                }
                return newProgress;
            });
        }, updateInterval);

        return () => {
            clearInterval(timer);
        };
    }, []);

    return (
        <div className="flex flex-col gap-1 w-full">
            <div className="flex justify-between items-center">
                <p><b>Name:</b> {thing.name}</p>
                <button onClick={onDismiss} className="ml-4 p-1 rounded-full hover:bg-gray-200">
                    &#x2715;
                </button>
            </div>
            <p><b>Thing ID:</b> {thing.id}</p>
            <p><b>Int Cam ID:</b> {thing.username}</p>
            <p><b>Gen Desc:</b> {thing.general_description}</p>
            <p><b>Loc Desc:</b> {thing.location_description}</p>
            <p><b>Type:</b> {thing.type}</p>
            {thing.lat_lng && (
                <p><b>Location:</b> {thing.lat_lng.coordinates.join(', ')}</p>
            )}
            <div className="w-full bg-gray-200 rounded-full h-1 mt-2">
                <div className="bg-blue-500 h-1 rounded-full" style={{ width: `${progress}%` }}></div>
            </div>
        </div>
    );
};

interface IntegrationsThingsTalkdownComponentProps {
    alertId?: string;
    locationId: number;
    things: IThing[];
    thingId?: string;
    cameras?: any[];
    cameraId?: string | number;
    allowFileUpload?: boolean;
    showFileDropZone?: boolean;
    shouldCreateNote?: boolean;
    orgId?: string;
}

interface LibrarySearchItem {
    file: TalkdownFile;
    scopeLabel: string;
    categoryName: string;
    groupLabel: string;
    searchText: string;
    score: number;
}

const SEARCH_SCOPE_ALIAS: Record<string, string> = {
    cam: "camera",
    camera: "camera",
    site: "site",
    int: "integration",
    integration: "integration",
    global: "global",
    all: "global",
};

const normalizeToken = (token: string) =>
    SEARCH_SCOPE_ALIAS[token.toLowerCase()] ?? token.toLowerCase();

const fuzzySubsequenceScore = (text: string, token: string): number => {
    if (!token) return 0;
    const haystack = text.toLowerCase();
    const needle = token.toLowerCase();
    if (haystack.includes(needle)) return 60 + Math.max(0, 20 - (haystack.indexOf(needle) / 2));

    let h = 0;
    let n = 0;
    let streak = 0;
    let score = 0;
    while (h < haystack.length && n < needle.length) {
        if (haystack[h] === needle[n]) {
            n += 1;
            streak += 1;
            score += 8 + streak;
        } else {
            streak = 0;
        }
        h += 1;
    }
    if (n !== needle.length) return -1;
    return score;
};

const highlightQueryText = (text: string, tokens: string[]) => {
    if (!tokens.length) return text;
    const normalized = text.toLowerCase();
    const ranges: Array<{ start: number; end: number }> = [];
    for (const token of tokens) {
        const t = token.toLowerCase();
        if (!t) continue;
        const idx = normalized.indexOf(t);
        if (idx >= 0) ranges.push({ start: idx, end: idx + t.length });
    }
    if (!ranges.length) return text;
    ranges.sort((a, b) => a.start - b.start);
    const merged: Array<{ start: number; end: number }> = [];
    for (const current of ranges) {
        const prev = merged[merged.length - 1];
        if (!prev || current.start > prev.end) merged.push(current);
        else prev.end = Math.max(prev.end, current.end);
    }

    const parts: React.ReactNode[] = [];
    let cursor = 0;
    merged.forEach((r, i) => {
        if (cursor < r.start) parts.push(<span key={`t-${i}-plain`}>{text.slice(cursor, r.start)}</span>);
        parts.push(
            <mark
                key={`t-${i}-mark`}
                className="bg-transparent text-slate-100 border border-sky-300/60 rounded-sm px-0.5"
            >
                {text.slice(r.start, r.end)}
            </mark>
        );
        cursor = r.end;
    });
    if (cursor < text.length) parts.push(<span key="t-tail">{text.slice(cursor)}</span>);
    return parts;
};

const IntegrationsThingsTalkdownComponent = ({
    alertId,
    locationId,
    things,
    thingId = '',
    cameras,
    cameraId = '',
    allowFileUpload = false,
    showFileDropZone = false,
    shouldCreateNote = false,
    orgId: orgIdProp
}: IntegrationsThingsTalkdownComponentProps) => {

    const activeCameras = useMemo(() => {
        if (!cameras || cameras.length === 0) return [];

        return cameras.filter((camera: ICamera) => {
            if (isAltaVirtualRoutingCamera(camera)) return false;
            if (camera.status !== 0 || !camera.CameraSchedules?.length) return false;
            return true;
        });
    }, [cameras]);
    const isCameraActive = activeCameras.some(camera => camera.id === Number(cameraId));

    const [recordingState, setRecordingState] = useState("stopped");
    const [selectedDevice, setSelectedDevice] = useState(thingId || (isCameraActive ? cameraId : undefined));
    const [selectedDeviceType, setSelectedDeviceType] = useState<'speaker' | 'camera' | null>(
        thingId ? 'speaker' : cameraId ? 'camera' : null
    );
    const [loading, setLoading] = useState(false);
    const [showAlphaDialog, setShowAlphaDialog] = useState(false);
    const [alphaDialogDeviceName, setAlphaDialogDeviceName] = useState<string | undefined>(undefined);
    
    // Enhanced status tracking
    const [operationStatus, setOperationStatus] = useState<{
        type: 'idle' | 'recording' | 'uploading' | 'processing' | 'sending' | 'complete' | 'error';
        message: string;
        progress: number;
        details?: string;
        timestamp?: Date;
    }>({
        type: 'idle',
        message: 'Ready to perform talkdown',
        progress: 0
    });
    
    const [showStatusDialog, setShowStatusDialog] = useState(false);
    const [lastOperationResult, setLastOperationResult] = useState<{
        success: boolean;
        message: string;
        details: string;
        operationType: string;
        deviceName: string;
        timestamp: Date;
    } | null>(null);

    // Soundboard status tracking
    const [soundboardStatus, setSoundboardStatus] = useState<{
        isPlaying: boolean;
        currentItem: SoundboardItem | null;
        status: 'idle' | 'preparing' | 'sending' | 'success' | 'error';
        message: string;
        progress: number;
    }>({
        isPlaying: false,
        currentItem: null,
        status: 'idle',
        message: 'Ready to play sounds',
        progress: 0
    });

    // Speaker info tracking for selected camera
    const [speakerInfo, setSpeakerInfo] = useState<{
        speakers: Array<{
            id: number;
            camera_id: number;
            box_id: string;
            device_type: string;
        }>;
        loading: boolean;
        error: string | null;
    }>({
        speakers: [],
        loading: false,
        error: null
    });

    // Voice Forge / Talkdown Library
    const [libraryFiles, setLibraryFiles] = useState<TalkdownFile[]>([]);
    const [libraryCategories, setLibraryCategories] = useState<TalkdownCategory[]>([]);
    const [libraryLoading, setLibraryLoading] = useState(false);
    const [sendingLibraryId, setSendingLibraryId] = useState<number | null>(null);
    const [voiceForgeQuery, setVoiceForgeQuery] = useState("");
    const [voiceForgeOpen, setVoiceForgeOpen] = useState(false);
    const [highlightedIndex, setHighlightedIndex] = useState(-1);
    const [selectedLibraryId, setSelectedLibraryId] = useState<string>("");
    const [playingLibraryId, setPlayingLibraryId] = useState<number | null>(null);
    const [loadingLibraryAudioId, setLoadingLibraryAudioId] = useState<number | null>(null);
    const [previewAudioUrl, setPreviewAudioUrl] = useState<string | null>(null);
    const [previewCurrentTime, setPreviewCurrentTime] = useState(0);
    const [previewDuration, setPreviewDuration] = useState(0);
    const [cameraSpeakerSupport, setCameraSpeakerSupport] = useState<Record<string, boolean>>({});
    const [cameraSpeakerSupportLoading, setCameraSpeakerSupportLoading] = useState(false);
    const previewAudioRef = useRef<HTMLAudioElement | null>(null);
    const voiceForgeInputRef = useRef<HTMLInputElement | null>(null);
    const comboboxContainerRef = useRef<HTMLDivElement | null>(null);
    const dropdownRef = useRef<HTMLDivElement | null>(null);
    const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0, width: 0 });
    const previewCacheRef = useRef<Map<number, { url: string; createdAt: number }>>(new Map());
    const optionRefMap = useRef<Record<number, HTMLDivElement | null>>({});
    const milestoneTalkdownSessionIdRef = useRef<string | null>(null);
    const milestoneTalkdownUploadChainRef = useRef<Promise<void>>(Promise.resolve());
    const milestoneTalkdownUploadErrorRef = useRef<string | null>(null);
    const milestoneTalkdownPendingChunksRef = useRef<Uint8Array[]>([]);
    const milestoneTalkdownPendingByteLengthRef = useRef(0);
    const milestoneTalkdownMediaStreamRef = useRef<MediaStream | null>(null);
    const milestoneTalkdownAudioContextRef = useRef<AudioContext | null>(null);
    const milestoneTalkdownSourceNodeRef = useRef<MediaStreamAudioSourceNode | null>(null);
    const milestoneTalkdownProcessorRef = useRef<ScriptProcessorNode | null>(null);
    const milestoneTalkdownTargetDeviceRef = useRef<any>(null);
    const milestoneTalkdownModeRef = useRef(false);
    const speakerInfoRef = useRef(speakerInfo);
    const userSelectedDeviceRef = useRef(false);

    useEffect(() => {
        speakerInfoRef.current = speakerInfo;
    }, [speakerInfo]);

    const derivedOrgId = (() => {
        if (orgIdProp && orgIdProp.trim() !== "") return orgIdProp;
        if (selectedDeviceType === "camera" && selectedDevice && cameras?.length) {
            const cam = cameras.find((c: any) => String(c.id) === String(selectedDevice));
            const tid = (cam as any)?.teamId ?? (cam as any)?.TeamId;
            if (tid != null) return String(tid);
        }
        const first = cameras?.[0];
        const tid = (first as any)?.teamId ?? (first as any)?.TeamId;
        return tid != null ? String(tid) : "";
    })();

    console.log("[IntegrationsThingsTalkdownComponent] cameras : ",cameras)
    console.log("[IntegrationsThingsTalkdownComponent] activeCameras : ",cameras)

    useEffect(() => {
    let cancelled = false;

    if (!thingId && cameraId && !isCameraActive){
        const initialCamera = cameras?.find((cam : ICamera) => cam.id === Number(cameraId));
        toast.error(`Camera ${initialCamera?.name ?? ""} is disabled or does not have any schedules assigned.\n\nSelect a different camera from the drop-down for talk down.`,
            {duration: 10000}
        );
    }

    const checkCameraSpeakerSupport = async () => {
    if (!activeCameras || activeCameras.length === 0) {
      setCameraSpeakerSupport({});
      setCameraSpeakerSupportLoading(false);
      return;
    }

    setCameraSpeakerSupportLoading(true);
    try {
      const altaCameras = activeCameras.filter(isAvigilonAltaCamera);
      const altaSupportMap: Record<string, boolean> = {};

      if (altaCameras.length > 0) {
        try {
          const altaResponse = await getTalkdownCameraSpeakerSupport({
            cameras: altaCameras.map((camera: any) => ({
              cameraId: String(camera.id),
              vendorCameraId: getAltaVendorCameraId(camera),
              aipxGrainId: getAipxCameraGrainId(camera),
            })),
          });

          Object.assign(altaSupportMap, altaResponse?.cameraSupport ?? {});
        } catch (error) {
          console.error('[Camera dropdown] Avigilon Alta speaker support check failed:', error);
        }
      }

      // Avigilon Unity/ACC cameras are grouped by site (siteUniqueId), since the microservice
      // resolves capabilities per site credential; batch calls are split accordingly.
      const unityCameras = activeCameras.filter(isAvigilonUnityCamera);
      const unitySupportMap: Record<string, boolean> = {};

      if (unityCameras.length > 0) {
        const unityCamerasBySite = new Map<string, any[]>();
        for (const camera of unityCameras) {
          const siteUniqueId = getUnitySiteUniqueId(camera);
          if (!siteUniqueId) continue;
          if (!unityCamerasBySite.has(siteUniqueId)) unityCamerasBySite.set(siteUniqueId, []);
          unityCamerasBySite.get(siteUniqueId)!.push(camera);
        }

        await Promise.all(
          Array.from(unityCamerasBySite.entries()).map(async ([siteUniqueId, siteCameras]) => {
            try {
              const unityResponse = await getUnityTalkdownCameraSpeakerSupport({
                siteUniqueId,
                cameras: siteCameras.map((camera: any) => ({
                  cameraId: String(camera.id),
                  vendorCameraId: getUnityVendorCameraId(camera),
                })),
              });

              Object.assign(unitySupportMap, unityResponse?.cameraSupport ?? {});
            } catch (error) {
              console.error('[Camera dropdown] Avigilon Unity speaker support check failed:', siteUniqueId, error);
            }
          })
        );
      }

      const axisCameras = activeCameras.filter(isAxisCloudCamera);
      const axisSupportMap: Record<string, boolean> = {};

      if (axisCameras.length > 0) {
        const camerasByToken = axisCameras.reduce((acc: Record<string, any[]>, camera: any) => {
          const token = camera.password;
          if (!token) return acc;
          if (!acc[token]) acc[token] = [];
          acc[token].push(camera);
          return acc;
        }, {} as Record<string, any[]>);

        await Promise.all(
          Object.entries(camerasByToken).map(async ([aipxToken, cameras]) => {
            try {
              const axisResponse = await getAxisTalkdownCameraSpeakerSupport({
                AipxToken: aipxToken,
                Cameras: cameras.map((camera: any) => ({
                  CameraId: String(camera.id),
                  VendorCameraId: getAxisVendorCameraId(camera),
                })),
              });

              Object.assign(axisSupportMap, axisResponse?.cameraSupport ?? {});
            } catch (error) {
              console.error('[Camera dropdown] Axis Cloud speaker support check failed:', error);
            }
          })
        );
      }

      const results = await Promise.all(
        activeCameras.map(async (camera: any) => {
          if (isAvigilonAltaCamera(camera)) {
            return {
              cameraId: String(camera.id),
              hasSpeaker: altaSupportMap[String(camera.id)] === true
            };
          }

          if (isAvigilonUnityCamera(camera)) {
            return {
              cameraId: String(camera.id),
              hasSpeaker: unitySupportMap[String(camera.id)] === true
            };
          }

          if (isAxisCloudCamera(camera)) {
            return {
              cameraId: String(camera.id),
              hasSpeaker: axisSupportMap[String(camera.id)] === true
            };
          }

          const isEagleEyeCloud =
            camera?.location_description?.includes('EagleEyeCloud') ||
            camera?.general_description?.includes('EagleEyeCloud');

          const isHanwha =
            camera?.location_description?.includes('Hanwha') ||
            camera?.general_description?.includes('Hanwha');

          if (!isEagleEyeCloud && !isHanwha) {
            return { cameraId: String(camera.id), hasSpeaker: true };
          }

          if (isEagleEyeCloud) {
            const aipxToken = camera?.password ? camera.password.split('__')[0] : '';
            const eagleEyeCameraId = String(camera?.username || '');

            if (!aipxToken || !eagleEyeCameraId) {
              return { cameraId: String(camera.id), hasSpeaker: true };
            }

            try {
              const response = await getEagleEyeCloudSingleCameraAsync({
                AipxToken: aipxToken,
                CameraId: eagleEyeCameraId
              });

              const hasSpeaker = !!response?.data?.data?.capabilities?.audio?.speaker;
              return { cameraId: String(camera.id), hasSpeaker };
            } catch (error) {
              console.error('[Camera dropdown] EagleEye speaker check failed:', camera?.name, error);
              return { cameraId: String(camera.id), hasSpeaker: true };
            }
          }

          if (isHanwha) {
            const uniqueId = camera?.password ? String(camera.password) : '';
            const deviceId = String(camera?.username || '');

            if (!uniqueId || !deviceId) {
              return { cameraId: String(camera.id), hasSpeaker: true };
            }

            try {
              const response = await getHanwhaSingleCameraAsync({
                UniqueId: uniqueId,
                DeviceId: deviceId
              });

              const hasSpeaker = !!response?.data?.parameters?.twoWayAudioEnabled;
              return { cameraId: String(camera.id), hasSpeaker };
            } catch (error) {
              console.error('[Camera dropdown] Hanwha two-way audio check failed:', camera?.name, error);
              return { cameraId: String(camera.id), hasSpeaker: true };
            }
          }

          return { cameraId: String(camera.id), hasSpeaker: true };
        })
      );

      if (cancelled) return;

      const nextMap: Record<string, boolean> = {};
      for (const item of results) {
        nextMap[item.cameraId] = item.hasSpeaker;
      }

      setCameraSpeakerSupport(nextMap);
    } catch (error) {
      console.error('[Camera dropdown] checkCameraSpeakerSupport failed:', error);
    } finally {
      if (!cancelled) {
        setCameraSpeakerSupportLoading(false);
      }
    }
    };

    checkCameraSpeakerSupport();

    return () => {
        cancelled = true;
        };
    }, [cameras]);

    const visibleCameras = useMemo(() => {
        if (!activeCameras || activeCameras.length === 0) return [];

        return activeCameras.filter((camera: ICamera) => {
            if (!isAvigilonAltaCamera(camera)) return true;
            return cameraSpeakerSupport[String(camera.id)] === true;
        });
    }, [cameras, cameraSpeakerSupport]);

  const hanwhaCameras = useMemo(
        () => activeCameras.filter((camera: ICamera) => hasIntegrationName(camera, 'Hanwha')),
        [activeCameras]
    );

    const noHanwhaAudioDevices =
        (!things || things.length === 0) &&
        visibleCameras.length > 0 &&
        visibleCameras.every((camera: ICamera) => hasIntegrationName(camera, 'Hanwha')) &&
        hanwhaCameras.every((camera: ICamera) => cameraSpeakerSupport[String(camera.id)] === false);

    useEffect(() => {
        if (selectedDeviceType !== 'camera' || !selectedDevice) return;

        const selectedCamera = activeCameras?.find((camera: any) => String(camera.id) === String(selectedDevice));
        if (!selectedCamera) return;

        if (cameraSpeakerSupport[String(selectedCamera.id)] === false) {
            setSelectedDevice("");
            setSelectedDeviceType(null);
        }
    }, [cameraSpeakerSupport, cameras, selectedDevice, selectedDeviceType]);

    useEffect(() => {
        if (!VOICE_FORGE_ENABLED || !derivedOrgId.trim()) {
            setLibraryFiles([]);
            return;
        }
        setLibraryLoading(true);
        const selectedCam = selectedDeviceType === "camera" && selectedDevice && activeCameras?.length
            ? activeCameras.find((c: any) => String(c.id) === String(selectedDevice))
            : activeCameras?.[0];
        const grainId = selectedCam ? (selectedCam.externalCameraId ?? selectedCam.username ?? "") : "";
        const siteScopeId = locationId?.toString() ?? "";
        const vendorScopeId = selectedCam?.general_description ?? "";

        Promise.all([
            fetchTalkdownLibrary({ orgId: derivedOrgId, scopeType: "camera", scopeId: grainId }).catch(() => []),
            fetchTalkdownLibrary({ orgId: derivedOrgId, scopeType: "site", scopeId: siteScopeId }).catch(() => []),
            fetchTalkdownLibrary({ orgId: derivedOrgId, scopeType: "integration", scopeId: vendorScopeId }).catch(() => []),
            fetchTalkdownLibrary({ orgId: derivedOrgId, scopeType: "global" }).catch(() => []),
            fetchTalkdownCategories({ orgId: derivedOrgId }).catch(() => []),
        ])
            .then(([camFiles, siteFiles, intFiles, globalFiles, categories]) => {
                const merged = [...camFiles, ...siteFiles, ...intFiles, ...globalFiles];
                const seen = new Set<number>();
                const deduped = merged.filter((f) => {
                    if (seen.has(f.id)) return false;
                    seen.add(f.id);
                    return true;
                });
                const scopeOrder = new Map([
                    ["camera", 0],
                    ["site", 1],
                    ["integration", 2],
                    ["global", 3],
                ]);
                deduped.sort((a, b) =>
                    (scopeOrder.get(a.scopeType) ?? 99) - (scopeOrder.get(b.scopeType) ?? 99) ||
                    a.name.localeCompare(b.name)
                );
                setLibraryFiles(deduped);
                setLibraryCategories(categories);
            })
            .catch(() => {
                setLibraryFiles([]);
                setLibraryCategories([]);
            })
            .finally(() => setLibraryLoading(false));
    }, [derivedOrgId, selectedDevice, selectedDeviceType, cameras, locationId]);

    useEffect(() => {
        if (!selectedLibraryId) return;
        const exists = libraryFiles.some((file) => String(file.id) === selectedLibraryId);
        if (!exists) setSelectedLibraryId("");
    }, [libraryFiles, selectedLibraryId]);

    const scopeLabel = useCallback((scopeType: string) => {
        if (scopeType === "camera") return "Camera";
        if (scopeType === "site") return "Site";
        if (scopeType === "integration") return "Integration";
        return "Global";
    }, []);

    const searchTokens = useMemo(
        () => voiceForgeQuery.trim().split(/\s+/).map(normalizeToken).filter(Boolean),
        [voiceForgeQuery]
    );

    const filteredLibraryItems = useMemo<LibrarySearchItem[]>(() => {
        const categoryNameById = new Map<number, string>(
            libraryCategories.map((category) => [category.id, category.name])
        );
        const rows: LibrarySearchItem[] = [];

        for (const file of libraryFiles) {
            const categoryName = file.categoryId ? (categoryNameById.get(file.categoryId) ?? "Uncategorized") : "Uncategorized";
            const scope = scopeLabel(file.scopeType);
            const groupLabel = `${scope} - ${categoryName}`;
            const searchText = `${file.name} ${categoryName} ${scope} ${file.scopeType} ${file.textContent ?? ""}`.toLowerCase();
            let totalScore = 0;
            let valid = true;
            for (const token of searchTokens) {
                const tokenScore = fuzzySubsequenceScore(searchText, token);
                if (tokenScore < 0) {
                    valid = false;
                    break;
                }
                totalScore += tokenScore;
            }
            if (!valid) continue;
            rows.push({
                file,
                scopeLabel: scope,
                categoryName,
                groupLabel,
                searchText,
                score: searchTokens.length ? totalScore : 0,
            });
        }

        rows.sort((a, b) =>
            b.score - a.score ||
            a.scopeLabel.localeCompare(b.scopeLabel) ||
            a.categoryName.localeCompare(b.categoryName) ||
            a.file.name.localeCompare(b.file.name)
        );
        return rows;
    }, [libraryFiles, libraryCategories, searchTokens, scopeLabel]);

    const groupedLibraryOptions = useMemo(() => {
        const groups = new Map<string, LibrarySearchItem[]>();
        for (const row of filteredLibraryItems) {
            if (!groups.has(row.groupLabel)) groups.set(row.groupLabel, []);
            groups.get(row.groupLabel)!.push(row);
        }
        return Array.from(groups.entries()).map(([group, items]) => [group, items] as const);
    }, [filteredLibraryItems]);

    const flattenedLibraryOptions = useMemo(
        () => groupedLibraryOptions.flatMap(([, rows]) => rows),
        [groupedLibraryOptions]
    );

    const selectedLibraryLabel = useMemo(() => {
        if (!selectedLibraryId) return "";
        const selected = libraryFiles.find((file) => String(file.id) === selectedLibraryId);
        return selected?.name ?? "";
    }, [libraryFiles, selectedLibraryId]);

    useEffect(() => {
        if (!voiceForgeOpen) {
            setHighlightedIndex(-1);
            const audioEl = previewAudioRef.current;
            if (audioEl && !audioEl.paused) audioEl.pause();
            setPlayingLibraryId(null);
        }
    }, [voiceForgeOpen]);

    useEffect(() => {
        if (highlightedIndex < 0) return;
        const row = flattenedLibraryOptions[highlightedIndex];
        if (!row) return;
        const optionNode = optionRefMap.current[row.file.id];
        optionNode?.scrollIntoView({ block: "nearest" });
    }, [highlightedIndex, flattenedLibraryOptions]);

    useEffect(() => {
        const onMouseDown = (event: MouseEvent) => {
            if (!comboboxContainerRef.current) return;
            const target = event.target as Node;
            if (
                !comboboxContainerRef.current.contains(target) &&
                !dropdownRef.current?.contains(target)
            ) {
                setVoiceForgeOpen(false);
                if (!voiceForgeQuery.trim() && selectedLibraryLabel) {
                    setVoiceForgeQuery(selectedLibraryLabel);
                }
            }
        };
        document.addEventListener("mousedown", onMouseDown);
        return () => document.removeEventListener("mousedown", onMouseDown);
    }, [voiceForgeQuery, selectedLibraryLabel]);

    const updateDropdownPosition = useCallback(() => {
        const inputEl = voiceForgeInputRef.current;
        if (!inputEl) return;
        const rect = inputEl.getBoundingClientRect();
        setDropdownPosition({
            top: rect.bottom + 4,
            left: rect.left,
            width: rect.width,
        });
    }, []);

    useEffect(() => {
        if (!voiceForgeOpen) return;
        updateDropdownPosition();
        const handler = () => updateDropdownPosition();
        window.addEventListener("resize", handler);
        window.addEventListener("scroll", handler, true);
        return () => {
            window.removeEventListener("resize", handler);
            window.removeEventListener("scroll", handler, true);
        };
    }, [voiceForgeOpen, updateDropdownPosition]);

    useEffect(() => {
        return () => {
            if (previewAudioUrl) URL.revokeObjectURL(previewAudioUrl);
            previewCacheRef.current.forEach((entry) => URL.revokeObjectURL(entry.url));
            previewCacheRef.current.clear();
        };
    }, [previewAudioUrl]);

    useEffect(() => {
        userSelectedDeviceRef.current = false;
    }, [thingId, cameraId]);

    const prevThingIdRef = useRef(thingId);
    const prevCameraIdRef = useRef(cameraId);

    useEffect(() => {
        if (userSelectedDeviceRef.current) {
            prevThingIdRef.current = thingId;
            prevCameraIdRef.current = cameraId;
            return;
        }

        const thingChanged = thingId !== prevThingIdRef.current;
        const cameraChanged = cameraId !== prevCameraIdRef.current;
        prevThingIdRef.current = thingId;
        prevCameraIdRef.current = cameraId;

        const cameraExists = cameraId && activeCameras.find((cam: ICamera) => String(cam.id) === String(cameraId));
        const thingExists = thingId && things?.find((thing: IThing) => String(thing.id) === String(thingId));

        // Whichever prop actually changed reflects the device that was just
        // selected (e.g. in the parent camera list), so that one wins - not a
        // hardcoded device type. If both change together in the same update,
        // it's ambiguous which one the user meant, so leave the current
        // selection alone rather than guessing.
        if (thingChanged && cameraChanged) {
            return;
        }

        if (cameraChanged && !cameraExists) {
            setSelectedDevice("");
            setSelectedDeviceType(null);
        } else if (!cameraChanged && thingExists) {
            setSelectedDevice(thingId);
            setSelectedDeviceType('speaker');
        } else if (cameraExists) {
            setSelectedDevice(String(cameraId));
            setSelectedDeviceType('camera');
        }
    }, [thingId, cameraId, things, cameras]);

    const fetchSpeakersForCamera = useCallback(async (cameraUsername: string, token: string) => {
    // 1. Set loading state
        setLoading(true);
        setSpeakerInfo(prev => ({ ...prev, loading: true, error: null }));

        // 2. Execute the service call
        const data = await getSpeakersForCamera({ cameraUsername, token });

        // 3. Handle the response
        if (data.success) {
            setSpeakerInfo({
                speakers: data.speakers || [],
                loading: false,
                error: null
            });
        } else {
            setSpeakerInfo({
                speakers: [],
                loading: false,
                error: data.error || 'Failed to fetch speakers'
            });
        }
        setLoading(false);
    }, []);

    const autoStopTimeoutRef = React.useRef<NodeJS.Timeout | null>(null);

    const clearAutoStopTimeout = useCallback(() => {
        console.log("clearAutoStopTimeout called");
        if (autoStopTimeoutRef.current) {
            clearTimeout(autoStopTimeoutRef.current);
            autoStopTimeoutRef.current = null;
        }
    }, []);

    const resolveSelectedTargetDevice = useCallback(() => {
        if (!selectedDevice) {
            return null;
        }

        if (selectedDeviceType === 'speaker' && things) {
            return things.find((t: IThing) => String(t.id) === String(selectedDevice)) || null;
        }

        if (selectedDeviceType === 'camera' && activeCameras) {
            return activeCameras.find((c: any) => String(c.id) === String(selectedDevice)) || null;
        }

        return null;
    }, [selectedDevice, selectedDeviceType, things, cameras]);

    const cleanupMilestoneTalkdownResources = useCallback(() => {
        try {
            milestoneTalkdownSourceNodeRef.current?.disconnect();
        } catch (_) {
        }

        try {
            milestoneTalkdownProcessorRef.current?.disconnect();
        } catch (_) {
        }

        if (milestoneTalkdownMediaStreamRef.current) {
            milestoneTalkdownMediaStreamRef.current.getTracks().forEach((track) => track.stop());
        }

        if (milestoneTalkdownAudioContextRef.current) {
            milestoneTalkdownAudioContextRef.current.close().catch(() => {});
        }

        milestoneTalkdownMediaStreamRef.current = null;
        milestoneTalkdownAudioContextRef.current = null;
        milestoneTalkdownSourceNodeRef.current = null;
        milestoneTalkdownProcessorRef.current = null;
        milestoneTalkdownModeRef.current = false;
    }, []);

    const enqueueMilestoneTalkdownChunk = useCallback((chunk: Uint8Array) => {
        const sessionId = milestoneTalkdownSessionIdRef.current;
        if (!sessionId || chunk.byteLength <= 0 || milestoneTalkdownUploadErrorRef.current) {
            return;
        }

        const chunkCopy = new Uint8Array(chunk);
        const previousUpload = milestoneTalkdownUploadChainRef.current.catch(() => undefined);

        milestoneTalkdownUploadChainRef.current = previousUpload.then(async () => {
            if (milestoneTalkdownUploadErrorRef.current) {
                return;
            }

            const result = await sendLiveTalkdownChunk(sessionId, chunkCopy);
            if (!result.success) {
                throw new Error(result.message || "Live talkdown audio chunk failed.");
            }
        }).catch((error: any) => {
            milestoneTalkdownUploadErrorRef.current = error?.message || "Live talkdown audio chunk failed.";
            throw error;
        });

        milestoneTalkdownUploadChainRef.current.catch(() => undefined);
    }, []);

    const flushMilestoneTalkdownPendingAudio = useCallback((force = false) => {
        const pendingBytes = milestoneTalkdownPendingByteLengthRef.current;
        if (pendingBytes <= 0 || (!force && pendingBytes < MILESTONE_TALKDOWN_UPLOAD_CHUNK_BYTES)) {
            return;
        }

        const combined = new Uint8Array(pendingBytes);
        let offset = 0;

        for (const chunk of milestoneTalkdownPendingChunksRef.current) {
            combined.set(chunk, offset);
            offset += chunk.byteLength;
        }

        milestoneTalkdownPendingChunksRef.current = [];
        milestoneTalkdownPendingByteLengthRef.current = 0;
        enqueueMilestoneTalkdownChunk(combined);
    }, [enqueueMilestoneTalkdownChunk]);

    const appendMilestoneTalkdownAudio = useCallback((pcmBytes: Uint8Array) => {
        if (pcmBytes.byteLength <= 0) {
            return;
        }

        milestoneTalkdownPendingChunksRef.current.push(new Uint8Array(pcmBytes));
        milestoneTalkdownPendingByteLengthRef.current += pcmBytes.byteLength;
        flushMilestoneTalkdownPendingAudio(false);
    }, [flushMilestoneTalkdownPendingAudio]);

    const startMilestoneLiveRecording = useCallback(async (targetDevice: any) => {
        const milestoneDeviceId = targetDevice?.username?.toString?.() || "";
        if (!milestoneDeviceId) {
            throw new Error("Milestone camera identifier is missing.");
        }

        const browserWindow = window as Window & typeof globalThis & {
            webkitAudioContext?: typeof AudioContext;
        };
        const AudioContextCtor = browserWindow.AudioContext || browserWindow.webkitAudioContext;
        if (!AudioContextCtor) {
            throw new Error("This browser does not support live audio capture.");
        }

        const mediaStream = await navigator.mediaDevices.getUserMedia({
            audio: true,
            video: false
        });
        const audioContext = new AudioContextCtor();
        const sourceNode = audioContext.createMediaStreamSource(mediaStream);
        const processorNode = audioContext.createScriptProcessor(MILESTONE_TALKDOWN_BUFFER_SIZE, 1, 1);

        const sessionResult = await startLiveTalkdownSession(milestoneDeviceId);
        const sessionId = sessionResult.data?.sessionId || sessionResult.data?.SessionId;
        if (!sessionResult.success || !sessionId) {
            try {
                sourceNode.disconnect();
                processorNode.disconnect();
            } catch (_) {
            }
            mediaStream.getTracks().forEach((track) => track.stop());
            await audioContext.close().catch(() => {});
            throw new Error(sessionResult.message || "Failed to start Milestone live talkdown session.");
        }

        milestoneTalkdownSessionIdRef.current = sessionId;
        milestoneTalkdownTargetDeviceRef.current = targetDevice;
        milestoneTalkdownModeRef.current = true;
        milestoneTalkdownUploadErrorRef.current = null;
        milestoneTalkdownUploadChainRef.current = Promise.resolve();
        milestoneTalkdownPendingChunksRef.current = [];
        milestoneTalkdownPendingByteLengthRef.current = 0;

        processorNode.onaudioprocess = (event) => {
            if (!milestoneTalkdownSessionIdRef.current || milestoneTalkdownUploadErrorRef.current) {
                return;
            }

            const inputData = event.inputBuffer.getChannelData(0);
            const pcmBytes = convertFloat32ToMilestonePcm(
                new Float32Array(inputData),
                audioContext.sampleRate
            );

            appendMilestoneTalkdownAudio(pcmBytes);
        };

        sourceNode.connect(processorNode);
        processorNode.connect(audioContext.destination);

        milestoneTalkdownMediaStreamRef.current = mediaStream;
        milestoneTalkdownAudioContextRef.current = audioContext;
        milestoneTalkdownSourceNodeRef.current = sourceNode;
        milestoneTalkdownProcessorRef.current = processorNode;
    }, [appendMilestoneTalkdownAudio]);

    const stopMilestoneLiveRecording = useCallback(async () => {
        const targetDevice = milestoneTalkdownTargetDeviceRef.current;
        const sessionId = milestoneTalkdownSessionIdRef.current;

        cleanupMilestoneTalkdownResources();
        flushMilestoneTalkdownPendingAudio(true);
        setRecordingState("stopped");

        if (!sessionId || !targetDevice) {
            milestoneTalkdownSessionIdRef.current = null;
            milestoneTalkdownTargetDeviceRef.current = null;
            return;
        }

        updateStatus('processing', 'Finalizing live talkdown...', 80, `Target: ${targetDevice.name}`);
        setLoading(true);
        let sessionStopped = false;

        try {
            await milestoneTalkdownUploadChainRef.current;

            if (milestoneTalkdownUploadErrorRef.current) {
                throw new Error(milestoneTalkdownUploadErrorRef.current);
            }

            const result = await stopLiveTalkdownSession(sessionId);
            sessionStopped = true;

            if (result.success) {
                updateStatus('complete', 'Live talkdown completed successfully!', 100);
                showOperationResult(
                    true,
                    'Live Talkdown Successful',
                    `Live talkdown audio has been successfully sent to ${targetDevice.name}.`,
                    'Live Talkdown',
                    targetDevice.name
                );
                toast.success(result.message || 'Live talkdown completed successfully');
            } else {
                updateStatus('error', 'Live talkdown failed', 0, result.message);
                showOperationResult(
                    false,
                    'Live Talkdown Failed',
                    `Failed to perform live talkdown on ${targetDevice.name}. ${result.message || 'Unknown error occurred.'}`,
                    'Live Talkdown',
                    targetDevice.name
                );
                toast.error(result.message || 'Live talkdown failed');
            }
        } catch (error: any) {
            updateStatus('error', 'Live talkdown failed', 0, error?.message || 'Unexpected error');
            showOperationResult(
                false,
                'Live Talkdown Error',
                `Network or server error occurred while performing live talkdown on ${targetDevice.name}. ${error?.message || 'Unexpected error.'}`,
                'Live Talkdown',
                targetDevice.name
            );
            toast.error(error?.message || 'Live talkdown failed');
        } finally {
            try {
                if (!sessionStopped) {
                    await stopLiveTalkdownSession(sessionId);
                }
            } catch (_) {
            }

            milestoneTalkdownSessionIdRef.current = null;
            milestoneTalkdownTargetDeviceRef.current = null;
            milestoneTalkdownPendingChunksRef.current = [];
            milestoneTalkdownPendingByteLengthRef.current = 0;
            milestoneTalkdownUploadErrorRef.current = null;
            milestoneTalkdownUploadChainRef.current = Promise.resolve();
            setLoading(false);
            setTimeout(() => {
                updateStatus('idle', 'Ready to perform talkdown', 0);
            }, 2000);
        }
    }, [cleanupMilestoneTalkdownResources, flushMilestoneTalkdownPendingAudio]);

    useEffect(() => {
        return () => {
            clearAutoStopTimeout();
            const sessionId = milestoneTalkdownSessionIdRef.current;
            if (sessionId) {
                stopLiveTalkdownSession(sessionId).catch(() => {});
            }
            milestoneTalkdownSessionIdRef.current = null;
            cleanupMilestoneTalkdownResources();
        };
    }, [clearAutoStopTimeout, cleanupMilestoneTalkdownResources]);

    // Fetch speakers when a camera is selected
    useEffect(() => {
        // Only fetch speakers when a camera is selected AND we're in camera device type
        if (selectedDeviceType === 'camera' && selectedDevice && activeCameras) {
            const selectedCamera = activeCameras.find((c: any) => String(c.id) === String(selectedDevice));

            // Only fetch speakers for TuringCloud cameras
            const isTuringCloud = selectedCamera?.general_description?.toLowerCase().includes('turingcloud');

            if (selectedCamera?.password && isTuringCloud) {
                // password field contains the Turing API token for TuringCloud cameras
                // username field contains the camera uniqueId/integration ID
                fetchSpeakersForCamera(selectedCamera.username?.toString() || '', selectedCamera.password);
            } else if (!selectedDevice) {
                // Reset speaker info for non-TuringCloud cameras or cameras without password
                setSpeakerInfo({ speakers: [], loading: false, error: null });
            }
        } else {
            // Reset speaker info when switching to speaker or no device selected
            setSpeakerInfo({ speakers: [], loading: false, error: null });
        }
    }, [selectedDevice, selectedDeviceType, cameras, fetchSpeakersForCamera]);

    // Status management helpers
    const updateStatus = (type: typeof operationStatus.type, message: string, progress: number = 0, details?: string) => {
        setOperationStatus({
            type,
            message,
            progress,
            details,
            timestamp: new Date()
        });
    };

    const showOperationResult = (success: boolean, message: string, details: string, operationType: string, deviceName: string) => {
        setLastOperationResult({
            success,
            message,
            details,
            operationType,
            deviceName,
            timestamp: new Date()
        });
        setShowStatusDialog(true);
    };

    const handleCustomRecording = async (file: File, targetDevice: any) => {
        setLoading(true);
        
        try {
            // Step 1: Preparing audio file
            updateStatus('processing', 'Preparing audio file for upload...', 10, `File: ${file.name} (${(file.size / 1024 / 1024).toFixed(2)} MB)`);
            
            const formData = new FormData();
            formData.append('audioFile', file);
            if (alertId) formData.append('alertId', alertId);
            formData.append('locationId', locationId.toString());
            formData.append('cameraId', targetDevice.id.toString());
            formData.append('cameraName', targetDevice.name || '');
            formData.append('cameraUsername', targetDevice.username?.toString() || '');
            formData.append('cameraPassword', targetDevice.password || '');
            formData.append('cameraGeneralDescription', targetDevice.general_description || '');
            formData.append('cameraLocationDescription', targetDevice.location_description || '');
            formData.append('shouldCreateNote', shouldCreateNote ? 'true' : 'false');
            formData.append('speakers', JSON.stringify(speakerInfoRef.current.speakers))
            
            console.log('📤 FormData contents:', {
                audioFile: {
                    name: file.name,
                    type: file.type,
                    size: file.size
                },
                alertId,
                locationId,
                cameraId: targetDevice.id,
                cameraName: targetDevice.name,
                shouldCreateNote,
                speakers: JSON.stringify(speakerInfo.speakers)
            });
            
            // Step 2: Uploading to server
            updateStatus('uploading', 'Uploading audio file to server...', 30, `Target: ${targetDevice.name}`);
            
            const result = await universalPreRecordedAudio(formData) as any;
            
            // Step 3: Processing response
            updateStatus('processing', 'Processing server response...', 80);
            
            if (result.success) {
                updateStatus('complete', 'Audio upload completed successfully!', 100);
                showOperationResult(
                    true,
                    'Custom Audio Upload Successful',
                    `Audio file "${file.name}" has been successfully uploaded and processed. The audio will be played through ${targetDevice.name}.`,
                    'Custom Audio Upload',
                    targetDevice.name
                );
                toast.success(result.message || 'Audio uploaded successfully');
            } else {
                updateStatus('error', 'Audio upload failed', 0, result.message);
                showOperationResult(
                    false,
                    'Custom Audio Upload Failed',
                    `Failed to upload audio file "${file.name}". ${result.message || 'Unknown error occurred.'}`,
                    'Custom Audio Upload',
                    targetDevice.name
                );
                toast.error(result.message || 'Audio upload failed');
            }
        } catch (error: any) {
            updateStatus('error', 'Audio upload failed', 0, error.message);
            showOperationResult(
                false,
                'Custom Audio Upload Error',
                `Network or server error occurred while uploading "${file.name}". ${error.message}`,
                'Custom Audio Upload',
                targetDevice.name
            );
            toast.error(`Audio upload failed: ${error.message}`);
        } finally {
            setLoading(false);
            // Reset to idle after a delay
            setTimeout(() => {
                updateStatus('idle', 'Ready to perform talkdown', 0);
            }, 2000);
        }
    };

    const handlePreRecordedAudio = async (type: string, targetDevice: any) => {
        setLoading(true);
        
        try {
            // Step 1: Preparing pre-recorded audio request
            updateStatus('processing', 'Preparing pre-recorded audio request...', 20, `Type: ${type}, Target: ${targetDevice.name}`);
            
            const payload = {
                ...(alertId && { alertId }), // Only include alertId if it exists
                locationId,
                cameraId: targetDevice.id,
                cameraName: targetDevice.name,
                cameraUsername: targetDevice.username,
                cameraPassword: targetDevice.password,
                cameraGeneralDescription: targetDevice.general_description,
                cameraLocationDescription: targetDevice.location_description,
                type,
                shouldCreateNote: shouldCreateNote, // Only create notes when explicitly requested
                speakers: JSON.stringify(speakerInfo.speakers)
            };
            
            // Step 2: Sending request to server
            updateStatus('sending', 'Sending pre-recorded audio request...', 50, `Connecting to ${targetDevice.name}`);
            
            const result = await universalPreRecordedAudio(payload) as any;
            
            // Step 3: Processing response
            updateStatus('processing', 'Processing server response...', 80);
            
            if (result.success) {
                updateStatus('complete', 'Pre-recorded audio sent successfully!', 100);
                showOperationResult(
                    true,
                    'Pre-recorded Audio Sent Successfully',
                    `Pre-recorded audio of type "${type}" has been successfully sent to ${targetDevice.name}. The audio should now be playing through the device.`,
                    'Pre-recorded Audio',
                    targetDevice.name
                );
                toast.success(result.message || 'Pre-recorded audio sent successfully');
            } else {
                updateStatus('error', 'Pre-recorded audio failed', 0, result.message);
                showOperationResult(
                    false,
                    'Pre-recorded Audio Failed',
                    `Failed to send pre-recorded audio of type "${type}" to ${targetDevice.name}. ${result.message || 'Unknown error occurred.'}`,
                    'Pre-recorded Audio',
                    targetDevice.name
                );
                toast.error(result.message || 'Pre-recorded audio failed');
            }
        } catch (error: any) {
            updateStatus('error', 'Pre-recorded audio failed', 0, error.message);
            showOperationResult(
                false,
                'Pre-recorded Audio Error',
                `Network or server error occurred while sending pre-recorded audio "${type}" to ${targetDevice.name}. ${error.message}`,
                'Pre-recorded Audio',
                targetDevice.name
            );
            toast.error(`Pre-recorded audio failed: ${error.message}`);
        } finally {
            setLoading(false);
            // Reset to idle after a delay
            setTimeout(() => {
                updateStatus('idle', 'Ready to perform talkdown', 0);
            }, 2000);
        }
    };

    const handleFileUpload = useCallback(async (file: File) => {
        if (!selectedDevice) {
            toast.error("Please select a device to perform a talkdown from.");
            return;
        }
        
        let targetDevice = null;
        
        if (selectedDeviceType === 'speaker' && things) {
            const selectedThingObj = things.find((t: IThing) => String(t.id) === String(selectedDevice));
            if (!selectedThingObj) {
                return toast.error("Could not find selected speaker details.");
            }
            targetDevice = selectedThingObj;
        } else if (selectedDeviceType === 'camera' && activeCameras) {
            const selectedCameraObj = activeCameras.find((c: any) => String(c.id) === String(selectedDevice));
            if (!selectedCameraObj) {
                return toast.error("Could not find selected camera details.");
            }
            targetDevice = selectedCameraObj;
        }
        
        if (targetDevice) {
            if (selectedDeviceType === 'camera' && isAlphaCamera(targetDevice)) {
                setAlphaDialogDeviceName(targetDevice.name);
                setShowAlphaDialog(true);
                return;
            }
            await handleCustomRecording(file, targetDevice);
        }
    }, [selectedDevice, selectedDeviceType, things, cameras, alertId, locationId]);

    const logRecordingAudioAction = async (
        eventType: UserLogExtendedActionEventTypes,
        targetDevice: any,
        selectedDeviceType: "speaker" | "camera"
        ) => {
        try {
            await logUserAction(
            eventType,
            `User clicked recording audio button for ${selectedDeviceType} ${targetDevice?.id ?? ""} - ${targetDevice?.name ?? ""}`
            );
        } catch (err: any) {
            console.error(`Error logging recording audio action: ${err.message}`);
        }
};
    
    const startRecording = useCallback(async () => {
        if (!selectedDevice) {
            toast.error("Unable to start Talkdown recording. Please select a device to perform a talkdown from.");
            return;
        }

        const targetDevice = resolveSelectedTargetDevice();
        if (!targetDevice) {
            toast.error("Unable to find the selected device for talkdown.");
            return;
        }

        if (selectedDeviceType === 'camera' && isAlphaCamera(targetDevice)) {
            setAlphaDialogDeviceName(targetDevice.name);
            setShowAlphaDialog(true);
            return;
        }
        
        clearAutoStopTimeout();
        
        setRecordingState("waiting");
        updateStatus('recording', 'Initializing microphone...', 10, 'Setting up audio recording');
        
        try {
            if (selectedDeviceType === 'camera' && isMilestoneDevice(targetDevice)) {
                await startMilestoneLiveRecording(targetDevice);
                setRecordingState("recording");
                updateStatus('recording', 'Live talkdown in progress...', 50, 'Speak into your microphone');
                toast.success("Live talkdown started - speak into your microphone");

                autoStopTimeoutRef.current = setTimeout(() => {
                    console.log("Time expired for recording");
                    stopRecording();
                }, 1000 * 10);

                return;
            }

            await recorder.start();
            setRecordingState("recording");
            updateStatus('recording', 'Recording in progress...', 50, 'Speak into your microphone');
            toast.success("Recording started - speak into your microphone");

        autoStopTimeoutRef.current = setTimeout(() => {
            console.log("Time expired for recording");
            stopRecording();
        }, 1000 * 10);


        } catch (error) {
            console.error(error);
            const sessionId = milestoneTalkdownSessionIdRef.current;
            if (sessionId) {
                stopLiveTalkdownSession(sessionId).catch(() => {});
            }
            cleanupMilestoneTalkdownResources();
            milestoneTalkdownSessionIdRef.current = null;
            milestoneTalkdownTargetDeviceRef.current = null;
            milestoneTalkdownPendingChunksRef.current = [];
            milestoneTalkdownPendingByteLengthRef.current = 0;
            milestoneTalkdownUploadErrorRef.current = null;
            milestoneTalkdownUploadChainRef.current = Promise.resolve();
            setRecordingState("stopped");
            updateStatus('error', 'Failed to start recording', 0, 'Microphone access denied or unavailable');
            toast.error(`Error starting Talkdown recording: ${error}`);
        }
    }, [selectedDevice, selectedDeviceType, resolveSelectedTargetDevice, clearAutoStopTimeout, startMilestoneLiveRecording, cleanupMilestoneTalkdownResources]);

    const stopRecording = useCallback(async () => {
        
        clearAutoStopTimeout();

        if (milestoneTalkdownModeRef.current) {
            await stopMilestoneLiveRecording();
            return;
        }

        try {
            updateStatus('processing', 'Stopping recording and processing audio...', 70, 'Converting to MP3 format');
            
            const audio = await recorder.stop();
            setRecordingState("stopped");
            
            if (!selectedDevice) {
                updateStatus('error', 'Recording stopped but device selection lost', 0, 'Unable to process recording');
                toast.error("Unable to stop recording, lost device selection.");
                return;
            }
            
            updateStatus('processing', 'Converting audio to MP3...', 80, 'Preparing file for upload');
            
            const [buffer, blob] = await audio.getMp3();
            console.log('🎤 Recording blob details:', {
                type: blob.type,
                size: blob.size,
                bufferLength: buffer.length
            });
            
            const file = new File(buffer, "custom-recording.mp3", {
                type: blob.type || 'audio/mpeg', // Fallback to audio/mpeg if type is undefined
                lastModified: Date.now(),
            });
            
            console.log('📁 Created file details:', {
                name: file.name,
                type: file.type,
                size: file.size
            });
            
            let targetDevice = null;
            
            if (selectedDeviceType === 'speaker' && things) {
                const selectedThingObj = things.find((t: IThing) => String(t.id) === String(selectedDevice));
                if (!selectedThingObj) {
                    updateStatus('error', 'Speaker not found', 0, 'Could not locate selected speaker details');
                    return toast.error("Could not find selected speaker details.");
                }
                targetDevice = selectedThingObj;
            } else if (selectedDeviceType === 'camera' && activeCameras) {
                const selectedCameraObj = activeCameras.find((c: any) => String(c.id) === String(selectedDevice));
                if (!selectedCameraObj) {
                    updateStatus('error', 'Camera not found', 0, 'Could not locate selected camera details');
                    return toast.error("Could not find selected camera details.");
                }
                targetDevice = selectedCameraObj;
            }
            
            if (targetDevice) {
                updateStatus('processing', 'Ready to upload recording...', 90, `Target: ${targetDevice.name}`);
                await handleCustomRecording(file, targetDevice);
            }
        } catch (error) {
            console.error(error);
            updateStatus('error', 'Failed to process recording', 0, 'Error occurred while stopping recording');
            toast.error(`Error stopping recording: ${error}`);
        }
    }, [selectedDevice, selectedDeviceType, things, cameras, alertId, locationId, clearAutoStopTimeout, stopMilestoneLiveRecording]);

    const handleRecordingAudioClick = useCallback(() => {
        if (["waiting"].includes(recordingState) || !selectedDevice || loading) {
            return;
        }

        if (!selectedDeviceType) {
            toast.error("Please select a device type.");
            return;
        }

        const targetDevice = resolveSelectedTargetDevice();

        if (targetDevice) {
            const eventType =
            recordingState === "stopped"
                ? UserLogExtendedActionEventTypes.RECORDING_AUDIO_STARTED
                : UserLogExtendedActionEventTypes.RECORDING_AUDIO_STOPPED;

            void logRecordingAudioAction(
            eventType,
            targetDevice,
            selectedDeviceType
            );
        }

        if (recordingState === "stopped") {
            void startRecording();
        } else if (recordingState === "recording") {
            void stopRecording();
        }
    }, [recordingState,selectedDevice,selectedDeviceType,loading,resolveSelectedTargetDevice,startRecording,stopRecording]);

    const logPreRecordedAudioClick = async (
        type: string,
        targetDevice: any,
        selectedDeviceType: "speaker" | "camera"
        ) => {
        try {
            await logUserAction(
            UserLogExtendedActionEventTypes.PRERECORDED_AUDIO_CLICKED,
            `User clicked pre-recorded audio "${type}" for ${selectedDeviceType} ${targetDevice?.id ?? ""} - ${targetDevice?.name ?? ""}`
            );
        } catch (err: any) {
            console.error(`Error logging pre-recorded audio click: ${err.message}`);
        }
    };

    const handlePreRecordedAudioClick = useCallback(async (type: string) => {
        if (!selectedDevice) {
            toast.error("Please select a device to perform a talkdown from.");
            return;
        }

        if (!selectedDeviceType) {
            toast.error("Please select a device type.");
            return;
        }

        let targetDevice = null;

        if (selectedDeviceType === "speaker" && things) {
            const selectedThingObj = things.find(
            (t: IThing) => String(t.id) === String(selectedDevice)
            );

            if (!selectedThingObj) {
            return toast.error("Could not find selected speaker details.");
            }

            targetDevice = selectedThingObj;
        } else if (selectedDeviceType === "camera" && activeCameras) {
            const selectedCameraObj = activeCameras.find(
            (c: any) => String(c.id) === String(selectedDevice)
            );

            if (!selectedCameraObj) {
            return toast.error("Could not find selected camera details.");
            }

            targetDevice = selectedCameraObj;
        }

        if (targetDevice) {
            void logPreRecordedAudioClick(type, targetDevice, selectedDeviceType);

            if (selectedDeviceType === "camera" && isAlphaCamera(targetDevice)) {
            setAlphaDialogDeviceName(targetDevice.name);
            setShowAlphaDialog(true);
            return;
            }

            await handlePreRecordedAudio(type, targetDevice);
        }
    }, [selectedDevice,selectedDeviceType,things,cameras,handlePreRecordedAudio]);

    const handleSendLibraryItem = useCallback(async (file: TalkdownFile) => {
        if (!VOICE_FORGE_ENABLED) return;
        if (!selectedDevice) {
            toast.error("Please select a device to perform a talkdown from.");
            return;
        }
        let targetDevice: any = null;
        if (selectedDeviceType === "speaker" && things) {
            const selectedThingObj = things.find((t: IThing) => String(t.id) === String(selectedDevice));
            if (!selectedThingObj) {
                toast.error("Could not find selected speaker details.");
                return;
            }
            targetDevice = selectedThingObj;
        } else if (selectedDeviceType === "camera" && activeCameras) {
            const selectedCameraObj = activeCameras.find((c: any) => String(c.id) === String(selectedDevice));
            if (!selectedCameraObj) {
                toast.error("Could not find selected camera details.");
                return;
            }
            targetDevice = selectedCameraObj;
        }
        if (!targetDevice) {
            toast.error("No target device found.");
            return;
        }
        if (selectedDeviceType === "camera" && isAlphaCamera(targetDevice)) {
            setAlphaDialogDeviceName(targetDevice.name);
            setShowAlphaDialog(true);
            return;
        }
        setSendingLibraryId(file.id);
        let blobUrl: string | null = null;
        try {
            blobUrl = await fetchTalkdownAudioUrl(file.id);
            const response = await fetch(blobUrl);
            const blob = await response.blob();
            const audioFile = new File([blob], `${file.name}.wav`, { type: "audio/wav" });
            await handleCustomRecording(audioFile, targetDevice);
        } catch (err: any) {
            toast.error(err?.message ?? "Failed to send library audio");
        } finally {
            if (blobUrl) URL.revokeObjectURL(blobUrl);
            setSendingLibraryId(null);
        }
    }, [selectedDevice, selectedDeviceType, things, cameras]);

    const handlePreviewLibraryItem = useCallback(async (file: TalkdownFile) => {
        if (!VOICE_FORGE_ENABLED) return;
        const audioEl = previewAudioRef.current;
        if (!audioEl) return;

        if (playingLibraryId === file.id) {
            audioEl.pause();
            setPlayingLibraryId(null);
            return;
        }

        setLoadingLibraryAudioId(file.id);
        let nextUrl: string | null = null;
        try {
            const cached = previewCacheRef.current.get(file.id);
            if (cached && (Date.now() - cached.createdAt) < PREVIEW_CACHE_TTL_MS) {
                nextUrl = cached.url;
            } else {
                if (cached) {
                    URL.revokeObjectURL(cached.url);
                    previewCacheRef.current.delete(file.id);
                }
                nextUrl = await fetchTalkdownAudioUrl(file.id);
                previewCacheRef.current.set(file.id, { url: nextUrl, createdAt: Date.now() });
            }
            setPreviewAudioUrl(nextUrl);
            audioEl.src = nextUrl;
            await audioEl.play();
            setPlayingLibraryId(file.id);
            setPreviewCurrentTime(0);
        } catch (err: any) {
            toast.error(err?.message ?? "Unable to play preview");
            setPlayingLibraryId(null);
        } finally {
            setLoadingLibraryAudioId(null);
        }
    }, [playingLibraryId, previewAudioUrl]);

    const handleVoiceForgeInputKeyDown = useCallback((event: React.KeyboardEvent<HTMLInputElement>) => {
        if (!flattenedLibraryOptions.length) return;

        if (event.key === "ArrowDown") {
            event.preventDefault();
            setVoiceForgeOpen(true);
            setHighlightedIndex((prev) =>
                prev >= flattenedLibraryOptions.length - 1 ? 0 : prev + 1
            );
        } else if (event.key === "ArrowUp") {
            event.preventDefault();
            setVoiceForgeOpen(true);
            setHighlightedIndex((prev) =>
                prev <= 0 ? flattenedLibraryOptions.length - 1 : prev - 1
            );
        } else if (event.key === "Enter" && highlightedIndex >= 0) {
            event.preventDefault();
            const picked = flattenedLibraryOptions[highlightedIndex]?.file;
            if (!picked) return;
            setSelectedLibraryId(String(picked.id));
            setVoiceForgeQuery(picked.name);
            setVoiceForgeOpen(false);
        } else if (event.key === "Escape") {
            setVoiceForgeOpen(false);
        }
    }, [flattenedLibraryOptions, highlightedIndex]);

    const handlePlaySoundboardItem = useCallback(async (item: SoundboardItem) => {
        console.log('🎵 handlePlaySoundboardItem called with:', {
            itemName: item.name,
            locationId,
            selectedDevice,
            hasFile: !!item.file
        });

        // Reset status and start playing
        setSoundboardStatus({
            isPlaying: true,
            currentItem: item,
            status: 'preparing',
            message: `Preparing "${item.name}" for playback...`,
            progress: 10
        });

        if (!selectedDevice) {
            const errorMsg = 'Please select a device to perform a talkdown from';
            console.error('❌', errorMsg);
            setSoundboardStatus(prev => ({
                ...prev,
                status: 'error',
                message: errorMsg,
                progress: 0
            }));
            toast.error(errorMsg);
            return;
        }

        let targetDevice = null;
        
        if (selectedDeviceType === 'speaker' && things) {
            const selectedThingObj = things.find((t: IThing) => String(t.id) === String(selectedDevice));
            if (!selectedThingObj) {
                const errorMsg = 'Could not find selected speaker details';
                setSoundboardStatus(prev => ({
                    ...prev,
                    status: 'error',
                    message: errorMsg,
                    progress: 0
                }));
                toast.error(errorMsg);
                return;
            }
            targetDevice = selectedThingObj;
        } else if (selectedDeviceType === 'camera' && cameras) {
            const selectedCameraObj = cameras.find((c: any) => String(c.id) === String(selectedDevice));
            if (!selectedCameraObj) {
                const errorMsg = 'Could not find selected camera details';
                setSoundboardStatus(prev => ({
                    ...prev,
                    status: 'error',
                    message: errorMsg,
                    progress: 0
                }));
                toast.error(errorMsg);
                return;
            }
            targetDevice = selectedCameraObj;
        }

        if (!targetDevice) {
            const errorMsg = 'No target device found';
            setSoundboardStatus(prev => ({
                ...prev,
                status: 'error',
                message: errorMsg,
                progress: 0
            }));
            toast.error(errorMsg);
            return;
        }

        if (selectedDeviceType === 'camera' && isAlphaCamera(targetDevice)) {
            setAlphaDialogDeviceName(targetDevice.name);
            setShowAlphaDialog(true);
            return;
        }
        try {
            // Update status - preparing data
            setSoundboardStatus(prev => ({
                ...prev,
                status: 'preparing',
                message: `Preparing audio data for "${item.name}"...`,
                progress: 25
            }));

            console.log('📤 Creating FormData for soundboard audio...');
            
            // Create FormData for the API call with all necessary device information
            const formData = new FormData();
            formData.append('audioFile', item.file);
            formData.append('locationId', locationId.toString());
            formData.append('cameraId', selectedDevice.toString());
            formData.append('cameraName', targetDevice.name || '');
            formData.append('cameraUsername', targetDevice.username?.toString() || '');
            formData.append('cameraPassword', targetDevice.password || '');
            formData.append('cameraGeneralDescription', targetDevice.general_description || '');
            formData.append('cameraLocationDescription', targetDevice.location_description || '');
            formData.append('shouldCreateNote', shouldCreateNote ? 'true' : 'false');
            formData.append('speakers', JSON.stringify(speakerInfoRef.current.speakers));
            
            // Update status - sending to API
            setSoundboardStatus(prev => ({
                ...prev,
                status: 'sending',
                message: `Sending "${item.name}" to ${targetDevice.name}...`,
                progress: 50
            }));

            console.log('🚀 Calling universalPreRecordedAudio API with device data:', {
                deviceName: targetDevice.name,
                deviceUsername: targetDevice.username,
                deviceGeneralDescription: targetDevice.general_description,
                deviceType: selectedDeviceType,
                speakers: JSON.stringify(speakerInfo.speakers)
            });
            
            // Call the universal pre-recorded audio API
            const result = await universalPreRecordedAudio(formData) as any;
            
            // Update status - success
            setSoundboardStatus(prev => ({
                ...prev,
                status: 'success',
                message: `"${item.name}" sent successfully to ${targetDevice.name}`,
                progress: 100
            }));

            console.log('✅ Soundboard audio sent successfully:', result);
            toast.success(`"${item.name}" sent to ${targetDevice.name}`);

            // Reset status after 3 seconds
            setTimeout(() => {
                setSoundboardStatus({
                    isPlaying: false,
                    currentItem: null,
                    status: 'idle',
                    message: 'Ready to play sounds',
                    progress: 0
                });
            }, 3000);

        } catch (error) {
            const errorMsg = `Failed to send "${item.name}": ${error instanceof Error ? error.message : 'Unknown error'}`;
            console.error('❌ Error sending soundboard audio:', error);
            
            setSoundboardStatus(prev => ({
                ...prev,
                status: 'error',
                message: errorMsg,
                progress: 0
            }));

            toast.error(errorMsg);

            // Reset status after 5 seconds on error
            setTimeout(() => {
                setSoundboardStatus({
                    isPlaying: false,
                    currentItem: null,
                    status: 'idle',
                    message: 'Ready to play sounds',
                    progress: 0
                });
            }, 5000);
        }
    }, [selectedDevice, selectedDeviceType, things, cameras, locationId, shouldCreateNote]);

    useEffect(() => {
        const handleKeyDown = (event: KeyboardEvent) => {
            if (["INPUT", "SELECT", "TEXTAREA"].includes((event.target as HTMLElement).tagName)) {
            return;
            }

            switch (event.key) {
            case "r":
                handleRecordingAudioClick();
                break;

            case "1":
                if (selectedDevice && !loading) {
                handlePreRecordedAudioClick("Precautionary");
                }
                break;

            case "2":
                if (selectedDevice && !loading) {
                handlePreRecordedAudioClick("Restricted");
                }
                break;

            default:
                break;
            }
        };

        window.addEventListener("keydown", handleKeyDown);

        return () => {
            window.removeEventListener("keydown", handleKeyDown);
        };
    }, [selectedDevice,loading,handleRecordingAudioClick,handlePreRecordedAudioClick]);
    return (
        <>
            <AlphaTalkdownDialog
                open={showAlphaDialog}
                onOpenChange={setShowAlphaDialog}
                cameraName={alphaDialogDeviceName}
            />
            <div className="flex w-full flex-col items-center justify-center gap-1">
                <div className="flex w-full items-center justify-between gap-3 px-2">
                    <div className="flex w-full items-center justify-center gap-3">
                        <HiSpeakerphone className="text-2xl text-white" />

                        {(things && things.length > 0) || visibleCameras.length > 0 ? (
                            <select
                                aria-label="Select a device for talkdown"
                                // Speaker (Thing) ids and camera ids are independent sequences and can
                                // collide (e.g. both having id 1). A plain numeric `value` would make the
                                // native <select> match/display whichever option (of either type) happens
                                // to share that id, regardless of which one is actually selected. Prefixing
                                // with the device type keeps every option's DOM value unique.
                                value={selectedDevice && selectedDeviceType ? `${selectedDeviceType}:${selectedDevice}` : ""}
                                data-qa="things-talkdown-device-select"
                                disabled={cameraSpeakerSupportLoading}
                                className="w-full rounded-md bg-gray-300 p-2 text-gray-900 disabled:bg-gray-500 disabled:text-gray-900 disabled:cursor-not-allowed"
                                onChange={(e: any) => {
                                    const selectedOption = e.target.selectedOptions[0];
                                    const deviceTypeAttr = selectedOption.dataset.deviceType as 'speaker' | 'camera' | undefined;
                                    const rawDeviceId = selectedOption.dataset.deviceId ?? "";

                                    if (selectedOption.dataset.json) {
                                        const device = JSON.parse(selectedOption.dataset.json);
                                        console.log(device);

                                        if (deviceTypeAttr === 'speaker') {
                                            toast.success(
                                                (t) => (
                                                    <ThingInfoToast
                                                        thing={device}
                                                        onDismiss={() => toast.dismiss(t.id)}
                                                    />
                                                ),
                                                { duration: 30000 }
                                            );
                                        } else if (deviceTypeAttr === 'camera') {
                                            const noSpeakerSupport = cameraSpeakerSupport[String(device.id)] === false;

                                            if (noSpeakerSupport) {
                                                toast.error(
                                                    (t) => (
                                                        <CameraInfoToast
                                                            camera={device}
                                                            noSpeakerSupport={true}
                                                            onDismiss={() => toast.dismiss(t.id)}
                                                        />
                                                    ),
                                                    { duration: 30000 }
                                                );

                                                return;
                                            }

                                            toast.success(
                                                (t) => (
                                                    <CameraInfoToast
                                                        camera={device}
                                                        onDismiss={() => toast.dismiss(t.id)}
                                                    />
                                                ),
                                                { duration: 30000 }
                                            );
                                        }
                                    }

                                    setSelectedDevice(rawDeviceId);
                                    setSelectedDeviceType(deviceTypeAttr ?? null);
                                    userSelectedDeviceRef.current = true;
                                }}
                            >
                                <option value={""}>
                                    Select a device to perform a talkdown from:
                                </option>

                                {things && things.length > 0 && (
                                    <optgroup label="Speakers">
                                        {things.map((thing: IThing) => (
                                            <option
                                                key={`speaker-${thing.id}`}
                                                value={`speaker:${thing.id}`}
                                                data-device-id={thing.id}
                                                data-json={JSON.stringify(thing)}
                                                data-device-type="speaker"
                                            >
                                                🔊 {thing.name}
                                            </option>
                                        ))}
                                    </optgroup>
                                )}

                            {visibleCameras.length > 0 && (
                                <optgroup label="Cameras">
                                    {visibleCameras.map((camera: any) => {
                                        const noSpeakerSupport = cameraSpeakerSupport[String(camera.id)] === false;
                                        const isHanwhaCamera =
                                            camera?.location_description?.includes('Hanwha') ||
                                            camera?.general_description?.includes('Hanwha');

                                        return (
                                            <option
                                                key={`camera-${camera.id}`}
                                                value={`camera:${camera.id}`}
                                                data-device-id={camera.id}
                                                data-json={JSON.stringify(camera)}
                                                data-device-type="camera"
                                            >
                                                {noSpeakerSupport
                                                    ? isHanwhaCamera
                                                        ? `🔇 📹 ${camera.name}`
                                                        : `🚫 📹 ${camera.name}`
                                                    : `📹 ${camera.name}`}
                                            </option>
                                        );
                                    })}
                                </optgroup>
                            )}
                            </select>
                        ) : (
                            <span>No devices available at this location</span>
                        )}
                    </div>
                    <Tooltip.Provider delayDuration={300} skipDelayDuration={1000}>
                        <Tooltip.Root>
                            <Tooltip.Trigger asChild>
                                <button
                                    data-qa="things-talkdown-record-button"
                                    onClick={handleRecordingAudioClick}
                                    type="button"
                                    className="items-center rounded-md border border-transparent bg-red-100 p-1 text-red-700 hover:bg-red-200 focus:outline-none disabled:bg-gray-500 disabled:text-gray-900"
                                    disabled={
                                    ["waiting"].includes(recordingState) || !selectedDevice || loading || cameraSpeakerSupportLoading
                                    }
                                >
                                    {recordingState === "stopped" ? (
                                    <BsFillRecordFill className="text-3xl" />
                                    ) : recordingState === "recording" ? (
                                    <BsFillStopFill className="animate-pulse text-3xl text-red-500" />
                                    ) : (
                                    <CiNoWaitingSign className="animate-pulse text-3xl text-red-500" />
                                    )}
                                </button>
                            </Tooltip.Trigger>
                            <Tooltip.Portal>
                                <Tooltip.Content
                                    align="center"
                                    side="top"
                                    sideOffset={2}
                                    className="rounded-sm bg-black/90 px-4 py-1 text-xs text-white"
                                >
                                    {recordingState === "stopped" ? "Record (R)" : "Stop (R)"}
                                </Tooltip.Content>
                            </Tooltip.Portal>
                        </Tooltip.Root>
                    </Tooltip.Provider>
                </div>
                {noHanwhaAudioDevices && (
                    <div className="w-full px-2">
                        <div className="p-3 bg-gray-50 border border-gray-200 rounded-md">
                            <p className="text-sm text-gray-600">
                                No devices at this location can send audio. Please check the two-way audio
                                capability and device connections.
                            </p>
                        </div>
                    </div>
                )}
                {recordingState === "recording" && (
                    <label className="w-full animate-pulse rounded-md bg-red-300 px-4 py-1 text-center text-xs font-light text-red-700">
                        {recordingState.toUpperCase()}
                    </label>
                )}
                {cameraSpeakerSupportLoading && (
                    <label className="w-full animate-pulse rounded-md bg-blue-200 px-4 py-1 text-center text-xs font-light text-blue-800">
                        Checking camera speaker support...
                    </label>
                )}
                {selectedDeviceType === 'camera' && (
                    <div className="mt-3">
                        {speakerInfo.loading && (
                            <div className="p-3 bg-blue-50 border border-blue-200 rounded-md">
                                <p className="text-sm text-blue-700">Loading connected speakers...</p>
                            </div>
                        )}

                        {!speakerInfo.loading && speakerInfo.error && (
                            <div className="p-3 bg-red-50 border border-red-200 rounded-md">
                                <p className="text-sm text-red-700">Error loading speakers: {speakerInfo.error}</p>
                            </div>
                        )}

                        {!speakerInfo.loading && !speakerInfo.error && speakerInfo.speakers.length > 0 && (
                            <div className="p-3 bg-green-50 border border-green-200 rounded-md">
                                <p className="text-sm font-semibold text-green-800 mb-2">Connected Speakers:</p>
                                <div className="flex flex-col gap-2">
                                    {speakerInfo.speakers.map((speaker, index) => (
                                        <div key={`${speaker.id}-${speaker.camera_id}-${index}`} className="flex items-center justify-between text-sm">
                                            <span className="font-medium text-gray-700">🔊 {speaker.box_id}</span>
                                            <span className="px-2 py-1 rounded text-xs font-medium bg-blue-200 text-blue-800">
                                                ID: {speaker.id}
                                            </span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {!speakerInfo.loading && !speakerInfo.error && speakerInfo.speakers.length === 0 && selectedDevice && (() => {
                            const selectedCamera = activeCameras?.find((c: any) => String(c.id) === String(selectedDevice));
                            const isTuringCloud = selectedCamera?.general_description?.toLowerCase().includes('turingcloud');
                            return isTuringCloud;
                        })() && (
                            <div className="p-3 bg-gray-50 border border-gray-200 rounded-md">
                                <p className="text-sm text-gray-600">No speakers connected to this camera</p>
                            </div>
                        )}
                    </div>
                )}

                <div className="flex w-full flex-col items-center gap-2">
                    <p className="mt-2 text-xs text-white dark:text-gray-200">
                        Pre-recorded Audio
                    </p>
                    <audio
                        ref={previewAudioRef}
                        onEnded={() => setPlayingLibraryId(null)}
                        onPause={() => setPlayingLibraryId(null)}
                        onTimeUpdate={(event) => setPreviewCurrentTime(event.currentTarget.currentTime || 0)}
                        onLoadedMetadata={(event) => setPreviewDuration(event.currentTarget.duration || 0)}
                    />
                    <div className="flex w-full items-center justify-center gap-2">
                        <Tooltip.Provider delayDuration={300} skipDelayDuration={1000}>
                            <Tooltip.Root>
                                <Tooltip.Trigger asChild>
                                    <button
                                        className="rounded-md bg-red-300 px-4 py-2 text-xs font-bold text-red-800 disabled:bg-gray-500 disabled:text-gray-900"
                                        type="button"
                                        onClick={() => handlePreRecordedAudioClick("Precautionary")}
                                        disabled={!selectedDevice || loading || cameraSpeakerSupportLoading}
                                        data-qa="things-talkdown-precautionary-audio-button"
                                    >
                                        <span role="img" aria-label="warning">⚠️</span> Precautionary
                                    </button>
                                </Tooltip.Trigger>
                                <Tooltip.Content
                                    align="center"
                                    side="top"
                                    sideOffset={2}
                                    className="rounded-sm bg-black/90 px-4 py-1 text-xs text-white"
                                >
                                    {"Use Precautionary Audio (1)"}
                                </Tooltip.Content>
                            </Tooltip.Root>
                            <Tooltip.Root>
                                <Tooltip.Trigger asChild>
                                    <button
                                        className="rounded-md bg-red-300 px-4 py-2 text-xs font-bold text-red-800 disabled:bg-gray-500 disabled:text-gray-900"
                                        type="button"
                                        onClick={() => handlePreRecordedAudioClick("Restricted")}
                                        disabled={!selectedDevice || loading || cameraSpeakerSupportLoading}
                                        data-qa="things-talkdown-restricted-audio-button"
                                    >
                                        <span role="img" aria-label="stop">🛑</span> Restricted
                                    </button>
                                </Tooltip.Trigger>
                                <Tooltip.Content
                                    align="center"
                                    side="top"
                                    sideOffset={2}
                                    className="rounded-sm bg-black/90 px-4 py-1 text-xs text-white"
                                >
                                    {"Use Restricted Audio (2)"}
                                </Tooltip.Content>
                            </Tooltip.Root>
                        </Tooltip.Provider>
                        {VOICE_FORGE_ENABLED && (
                        <div
                            ref={comboboxContainerRef}
                            className="relative w-full max-w-xl"
                        >
                            <input
                                ref={voiceForgeInputRef}
                                type="text"
                                value={voiceForgeQuery}
                                onFocus={() => setVoiceForgeOpen(true)}
                                onClick={() => setVoiceForgeOpen(true)}
                                onChange={(e) => {
                                    setVoiceForgeQuery(e.target.value);
                                    setVoiceForgeOpen(true);
                                }}
                                onKeyDown={handleVoiceForgeInputKeyDown}
                                placeholder={libraryLoading ? "Loading Voice Forge..." : "Search Voice Forge clips..."}
                                className="w-full rounded-md bg-gray-300 p-2 text-xs text-gray-900 placeholder:text-gray-500 disabled:bg-gray-500 disabled:text-gray-900"
                                disabled={libraryLoading}
                                role="combobox"
                                aria-expanded={voiceForgeOpen ? "true" : "false"}
                                aria-controls="voiceforge-listbox"
                                aria-autocomplete="list"
                                data-qa="things-talkdown-voiceforge-combobox"
                            />
                            <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center gap-1 pr-2 text-gray-600">
                                <span className="text-[10px]">{voiceForgeOpen ? "▴" : "▾"}</span>
                            </div>
                            {voiceForgeQuery.trim() && (
                                <button
                                    type="button"
                                    onMouseDown={(event) => {
                                        event.preventDefault();
                                        event.stopPropagation();
                                    }}
                                    onClick={() => {
                                        setVoiceForgeQuery("");
                                        setSelectedLibraryId("");
                                        setHighlightedIndex(-1);
                                        setVoiceForgeOpen(true);
                                        requestAnimationFrame(() => {
                                            voiceForgeInputRef.current?.focus();
                                        });
                                    }}
                                    className="absolute inset-y-0 right-5 my-auto h-5 w-5 rounded text-[10px] text-gray-700 hover:bg-gray-400/60"
                                    aria-label="Clear Voice Forge search"
                                    data-qa="things-talkdown-voiceforge-clear"
                                >
                                    ✕
                                </button>
                            )}
                            {voiceForgeOpen && createPortal(
                                <div
                                    id="voiceforge-listbox"
                                    ref={dropdownRef}
                                    role="listbox"
                                    style={{
                                        position: "fixed",
                                        top: dropdownPosition.top,
                                        left: dropdownPosition.left,
                                        width: dropdownPosition.width,
                                        maxHeight: "18rem",
                                        zIndex: 80,
                                    }}
                                    className="overflow-auto rounded-md border border-slate-500 bg-slate-800 shadow-lg"
                                >
                                    {groupedLibraryOptions.length === 0 && (
                                        <div className="px-3 py-2 text-xs text-gray-300">No matching Voice Forge clips</div>
                                    )}
                                    {groupedLibraryOptions.map(([groupLabel, rows]) => (
                                        <div key={groupLabel} className="border-b border-slate-700/60 last:border-b-0">
                                            <div className="sticky top-0 z-10 px-3 py-1 text-[10px] uppercase tracking-wide text-slate-300 bg-slate-900/95 backdrop-blur">
                                                {groupLabel}
                                            </div>
                                            {rows.map((row) => {
                                                const libFile = row.file;
                                                const flattenedIndex = flattenedLibraryOptions.findIndex((f) => f.file.id === libFile.id);
                                                const isHighlighted = highlightedIndex === flattenedIndex;
                                                const isSelected = selectedLibraryId === String(libFile.id);
                                                const isPlaying = playingLibraryId === libFile.id;
                                                const isLoadingPlay = loadingLibraryAudioId === libFile.id;
                                                const isSending = sendingLibraryId === libFile.id;
                                                return (
                                                    <div
                                                        key={libFile.id}
                                                        ref={(node) => {
                                                            optionRefMap.current[libFile.id] = node;
                                                        }}
                                                        role="option"
                                                        aria-selected={isSelected ? "true" : "false"}
                                                        className={`px-3 py-1.5 text-xs ${
                                                            isHighlighted || isSelected ? "bg-blue-700/40 text-white" : "text-slate-100 hover:bg-slate-700/70"
                                                        }`}
                                                        onMouseEnter={() => setHighlightedIndex(flattenedIndex)}
                                                        onClick={() => {
                                                            setSelectedLibraryId(String(libFile.id));
                                                            setVoiceForgeQuery(libFile.name);
                                                        }}
                                                    >
                                                        <div className="flex items-center gap-2">
                                                            <span className="rounded bg-slate-700/80 px-1.5 py-0.5 text-[9px] uppercase tracking-wide text-slate-200 border border-slate-500/60">
                                                                {row.scopeLabel}
                                                            </span>
                                                            <span className="min-w-0 flex-1 truncate">
                                                                {highlightQueryText(libFile.name, searchTokens)}
                                                            </span>
                                                            <span className="text-[10px] text-slate-300/80 hidden sm:inline">
                                                                {highlightQueryText(row.categoryName, searchTokens)}
                                                            </span>
                                                            <button
                                                                type="button"
                                                                onClick={(event) => {
                                                                    event.stopPropagation();
                                                                    handlePreviewLibraryItem(libFile);
                                                                }}
                                                                disabled={isLoadingPlay}
                                                                className="rounded bg-slate-600 px-2 py-1 text-[10px] text-white hover:bg-slate-500 disabled:opacity-50"
                                                                aria-label={`Play ${libFile.name}`}
                                                                data-qa="things-talkdown-voiceforge-play-button"
                                                            >
                                                                {isLoadingPlay ? "Loading..." : isPlaying ? "Pause" : "Play"}
                                                            </button>
                                                            <button
                                                                type="button"
                                                                onClick={(event) => {
                                                                    event.stopPropagation();
                                                                    handleSendLibraryItem(libFile);
                                                                }}
                                                                disabled={loading || isSending || !selectedDevice || cameraSpeakerSupportLoading}
                                                                className="rounded bg-blue-600 px-2 py-1 text-[10px] text-white hover:bg-blue-500 disabled:opacity-50"
                                                                aria-label={`Send ${libFile.name}`}
                                                                data-qa="things-talkdown-voiceforge-send-inline-button"
                                                            >
                                                                {isSending ? "Sending..." : "Send"}
                                                            </button>
                                                        </div>
                                                        {isPlaying && previewDuration > 0 && (
                                                            <div className="mt-1">
                                                                <div className="h-1 w-full rounded bg-slate-600/70">
                                                                    <div
                                                                        className="h-1 rounded bg-emerald-400"
                                                                        style={{ width: `${Math.min(100, (previewCurrentTime / previewDuration) * 100)}%` }}
                                                                    />
                                                                </div>
                                                                <div className="mt-0.5 text-[10px] text-slate-300">
                                                                    {previewCurrentTime.toFixed(1)}s / {previewDuration.toFixed(1)}s
                                                                </div>
                                                            </div>
                                                        )}
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    ))}
                                    {libraryLoading && groupedLibraryOptions.length === 0 && (
                                        <div className="px-3 py-2">
                                            <div className="h-3 w-24 rounded bg-slate-700/70 animate-pulse mb-2" />
                                            <div className="h-7 w-full rounded bg-slate-700/60 animate-pulse mb-1" />
                                            <div className="h-7 w-full rounded bg-slate-700/50 animate-pulse" />
                                        </div>
                                    )}
                                </div>,
                                document.body
                            )}
                        </div>
                        )}
                    </div>

                    {/* Library Audio (Voice Forge) */}
                    {VOICE_FORGE_ENABLED && (
                        <>
                            {libraryLoading && (
                                <p className="text-[10px] text-gray-400 mt-2 animate-pulse">Loading library…</p>
                            )}
                            {!libraryLoading && groupedLibraryOptions.length === 0 && (
                                <p className="text-[10px] text-gray-400 mt-2">No matching Voice Forge clips</p>
                            )}
                            {sendingLibraryId && (
                                <p className="text-[10px] text-blue-300 mt-1 animate-pulse">Sending Voice Forge clip…</p>
                            )}
                        </>
                    )}

                    {/* Soundboard Panel */}
                    {allowFileUpload && (
                        <div className="mt-4">
                            <SoundboardPanel
                                onPlaySound={handlePlaySoundboardItem}
                                className="mb-4"
                            />
                        </div>
                    )}

                    {/* Soundboard Status Display */}
                    {allowFileUpload && soundboardStatus && soundboardStatus.status !== 'idle' && (
                        <div className="bg-slate-700 rounded-lg border border-slate-600 p-4 mb-4">
                            <div className="flex items-center space-x-3 mb-3">
                                {soundboardStatus.status === 'preparing' && (
                                    <div className="w-5 h-5 border-2 border-blue-400 border-t-transparent rounded-full animate-spin" />
                                )}
                                {soundboardStatus.status === 'sending' && (
                                    <div className="w-5 h-5 border-2 border-yellow-400 border-t-transparent rounded-full animate-spin" />
                                )}
                                {soundboardStatus.status === 'success' && (
                                    <div className="w-5 h-5 bg-green-400 rounded-full flex items-center justify-center">
                                        <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                        </svg>
                                    </div>
                                )}
                                {soundboardStatus.status === 'error' && (
                                    <div className="w-5 h-5 bg-red-400 rounded-full flex items-center justify-center">
                                        <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                        </svg>
                                    </div>
                                )}
                                <span className="text-sm font-medium text-white">
                                    {soundboardStatus.status === 'preparing' && 'Preparing Audio'}
                                    {soundboardStatus.status === 'sending' && 'Sending Audio'}
                                    {soundboardStatus.status === 'success' && 'Audio Sent Successfully'}
                                    {soundboardStatus.status === 'error' && 'Error Sending Audio'}
                                </span>
                            </div>
                            
                            <p className="text-sm text-slate-300 mb-3">
                                {soundboardStatus.message}
                            </p>
                            
                            {(soundboardStatus.status === 'preparing' || soundboardStatus.status === 'sending') && (
                                <div className="w-full bg-slate-600 rounded-full h-2">
                                    <div
                                        className={`h-2 rounded-full transition-all duration-300 ${
                                            soundboardStatus.status === 'preparing' ? 'bg-blue-500' : 'bg-yellow-500'
                                        }`}
                                        style={{ width: `${soundboardStatus.progress}%` }}
                                    />
                                </div>
                            )}
                            
                            {soundboardStatus.currentItem && (
                                <div className="mt-3 text-xs text-slate-400">
                                    <span className="font-medium">Playing:</span> {soundboardStatus.currentItem.name}
                                </div>
                            )}
                        </div>
                    )}
                    
                    {showFileDropZone && allowFileUpload && (
                        <div className="mt-4">
                            <p className="text-xs text-white mb-2 text-center">
                                Or upload a custom audio file
                            </p>
                            <AudioFileDropZone
                                onFileDrop={handleFileUpload}
                                maxSize={50 * 1024 * 1024} // 50MB
                                acceptedTypes={['audio/mpeg', 'audio/wav', 'audio/mp3']}
                                disabled={!selectedDevice || loading || cameraSpeakerSupportLoading}
                            />
                        </div>
                    )}
                    
                    <div className="flex items-center justify-center gap-2">
                        <div className="flex items-center justify-center gap-2 mt-8">
                            <span className="text-xs text-white">
                                {selectedDeviceType === 'speaker' ? 'Speaker' : selectedDeviceType === 'camera' ? 'Camera' : 'Device'} ID: {selectedDevice || 'None'}
                            </span>
                            {selectedDeviceType && (
                                <span className="text-xs text-white">
                                    ({selectedDeviceType === 'speaker' ? '🔊' : '📹'} {selectedDeviceType})
                                </span>
                            )}
                        </div>
                    </div>
                </div>
            </div>
            
            {/* Status Display */}
            {operationStatus.type !== 'idle' && (
                <div className="mt-4 p-3 bg-slate-700 rounded-lg border border-slate-600">
                    <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                            <div className={`w-3 h-3 rounded-full ${
                                operationStatus.type === 'complete' ? 'bg-green-500' :
                                operationStatus.type === 'error' ? 'bg-red-500' :
                                operationStatus.type === 'recording' ? 'bg-red-500 animate-pulse' :
                                'bg-blue-500 animate-pulse'
                            }`} />
                            <span className="text-sm font-medium text-white">
                                {operationStatus.message}
                            </span>
                        </div>
                        <span className="text-xs text-gray-400">
                            {operationStatus.progress}%
                        </span>
                    </div>
                    
                    {/* Progress Bar */}
                    <div className="w-full bg-slate-600 rounded-full h-2 mb-2">
                        <div 
                            className={`h-2 rounded-full transition-all duration-300 ${
                                operationStatus.type === 'complete' ? 'bg-green-500' :
                                operationStatus.type === 'error' ? 'bg-red-500' :
                                'bg-blue-500'
                            }`}
                            style={{ width: `${operationStatus.progress}%` }}
                        />
                    </div>
                    
                    {/* Details */}
                    {operationStatus.details && (
                        <div className="text-xs text-gray-300">
                            {operationStatus.details}
                        </div>
                    )}
                    
                    {/* Timestamp */}
                    {operationStatus.timestamp && (
                        <div className="text-xs text-gray-500 mt-1">
                            {operationStatus.timestamp.toLocaleTimeString()}
                        </div>
                    )}
                </div>
            )}
            
            {/* Operation Result Dialog */}
            {showStatusDialog && lastOperationResult && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-slate-800 rounded-lg p-6 max-w-md w-full mx-4 border border-slate-600">
                        <div className="flex items-center gap-3 mb-4">
                            <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                                lastOperationResult.success ? 'bg-green-500' : 'bg-red-500'
                            }`}>
                                {lastOperationResult.success ? (
                                    <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                    </svg>
                                ) : (
                                    <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                    </svg>
                                )}
                            </div>
                            <h3 className="text-lg font-semibold text-white">
                                {lastOperationResult.message}
                            </h3>
                        </div>
                        
                        <div className="space-y-3 mb-6">
                            <div>
                                <span className="text-sm font-medium text-gray-300">Operation:</span>
                                <span className="text-sm text-white ml-2">{lastOperationResult.operationType}</span>
                            </div>
                            <div>
                                <span className="text-sm font-medium text-gray-300">Device:</span>
                                <span className="text-sm text-white ml-2">{lastOperationResult.deviceName}</span>
                            </div>
                            <div>
                                <span className="text-sm font-medium text-gray-300">Time:</span>
                                <span className="text-sm text-white ml-2">{lastOperationResult.timestamp.toLocaleString()}</span>
                            </div>
                            <div>
                                <span className="text-sm font-medium text-gray-300">Details:</span>
                                <p className="text-sm text-gray-200 mt-1">{lastOperationResult.details}</p>
                            </div>
                        </div>
                        
                        <div className="flex justify-end">
                            <button
                                onClick={() => setShowStatusDialog(false)}
                                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
                            >
                                Close
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default IntegrationsThingsTalkdownComponent;
